import {
  ConditionOperatorEnum,
  ScheduleMode,
  ScheduleType,
  type BackupPolicyConfig,
} from '@repo/api-gw-sdk';
import { DataClass, Environment, ResourceType } from '@repo/api-gw-sdk';

import type { DemoSettings } from '@/contexts/environment';

import { mockDalFactory } from '../../../utils';
import { vaultsDAL } from '../vaults/vaultsDAL';

const internalSystemsPolicy: BackupPolicyConfig = {
  id: '2b936bbf-4ba2-4c9d-b7f2-84f722051aaf',
  name: 'Internal Systems',
  enabled: true,
  userCreated: '',
  dateCreated: new Date(Date.now()),
  dateModified: new Date(Date.now()),
  include: [],
  exclude: [],
  attachedResources: 14,
  schedules: [
    {
      id: '5a7ab308-8990-4784-8365-bbb6911020a1',
      vaultId: '',
      windows: '0 0 * * *',
      duration: 4 * 60,
      backupRetention: 14,
      scheduleType: ScheduleType.SCHEDULE_TYPE_STANDARD,
    },
  ],
  scheduleMode: ScheduleMode.SCHEDULE_MODE_BASIC,
  condition: {
    operator: ConditionOperatorEnum.Or,
    conditions: [
      {
        property: 'environment',
        operator: ConditionOperatorEnum.In,
        value: [Environment.ProdInternal],
      },
      {
        property: 'tagKeys',
        operator: ConditionOperatorEnum.ContainsAnyOf,
        value: ['prod-internal'],
      },
      {
        property: 'tagKeyValues',
        operator: ConditionOperatorEnum.ContainsAnyOf,
        value: ['Env=prod-internal'],
      },
    ],
  },
};

const productionDatabasesPolicy: BackupPolicyConfig = {
  id: '049e49fa-9f7c-426b-9542-b6b2ab34ffad',
  name: 'Production Databases',
  exclude: [],
  include: [],
  userCreated: 'admin',
  dateCreated: new Date('2021-10-01T12:00:00Z'),
  dateModified: new Date('2021-10-01T12:00:00Z'),
  attachedResources: 23,
  schedules: [
    {
      windows: '0 0 * * *',
      backupRetention: 90,
      duration: 4 * 60,
      id: 'a92261dd-5926-4038-9bea-9b5b5e5c3b70',
      vaultId: '',
      scheduleType: ScheduleType.SCHEDULE_TYPE_STANDARD,
    },
  ],
  enabled: true,
  scheduleMode: ScheduleMode.SCHEDULE_MODE_BASIC,
};

const productionWithPIIPolicy: BackupPolicyConfig = {
  id: 'c42ab9fa-f477-4ff0-a722-d97528508cd2',
  name: 'Production with PII',
  enabled: true,
  userCreated: '',
  dateCreated: new Date(Date.now()),
  dateModified: new Date(Date.now()),
  include: [],
  exclude: [],
  attachedResources: 18,
  schedules: [
    {
      id: '5a7ab308-8990-4784-8364-kjdalksjdl2',
      vaultId: '',
      windows: '0 0 * * *',
      duration: 4 * 60,
      backupRetention: 90,
      scheduleType: ScheduleType.SCHEDULE_TYPE_STANDARD,
    },
    {
      id: '5a7ab308-8990-4784-8364-bbb6911020a2',
      vaultId: '',
      windows: '0 0 1 * *',
      duration: 4 * 60,
      backupRetention: 365,
      scheduleType: ScheduleType.SCHEDULE_TYPE_STANDARD,
    },
  ],
  condition: {
    operator: ConditionOperatorEnum.And,
    conditions: [
      {
        property: 'environment',
        operator: ConditionOperatorEnum.In,
        value: [Environment.Prod],
      },
      {
        property: 'dataClasses',
        operator: ConditionOperatorEnum.ContainsAnyOf,
        value: [DataClass.Fi, DataClass.Pii],
      },
    ],
  },
  scheduleMode: ScheduleMode.SCHEDULE_MODE_BASIC,
};

const productionWithoutPIIPolicy: BackupPolicyConfig = {
  id: '84f923051aaf',

  name: 'Production w/o PII',
  exclude: [],
  include: [],
  userCreated: 'admin',
  dateCreated: new Date('2021-10-01T12:00:00Z'),
  dateModified: new Date('2021-10-01T12:00:00Z'),
  attachedResources: 18,
  schedules: [
    {
      windows: '0 0 * * *',
      backupRetention: 45,
      duration: 4 * 60,
      id: '135245234',
      vaultId: '',
      scheduleType: ScheduleType.SCHEDULE_TYPE_STANDARD,
    },
  ],
  enabled: true,
  condition: {
    operator: ConditionOperatorEnum.And,
    conditions: [
      {
        property: 'environment',
        operator: ConditionOperatorEnum.In,
        value: [Environment.Prod],
      },
      {
        property: 'dataClasses',
        operator: ConditionOperatorEnum.ContainsNoneOf,
        value: [DataClass.Pii, DataClass.Fi],
      },
    ],
  },
  scheduleMode: ScheduleMode.SCHEDULE_MODE_BASIC,
};

const backupPoliciesData = [
  internalSystemsPolicy,
  productionDatabasesPolicy,
  productionWithPIIPolicy,
  productionWithoutPIIPolicy,
];

const fillDetails = (demoSettings: DemoSettings) => {
  const vaults = vaultsDAL.list(demoSettings);

  internalSystemsPolicy.schedules[0].vaultId = vaults[1].id;
  productionWithPIIPolicy.schedules[0].vaultId = vaults[1].id;
  productionWithPIIPolicy.schedules[1].vaultId = vaults[0].id;
  productionWithPIIPolicy.isSecurityScanEnabled = demoSettings.ransomware;
  productionWithoutPIIPolicy.schedules[0].vaultId = vaults[0].id;
  productionDatabasesPolicy.isSecurityScanEnabled = demoSettings.ransomware;
  productionDatabasesPolicy.schedules[0].vaultId = vaults[0].id;
  productionDatabasesPolicy.condition = {
    operator: ConditionOperatorEnum.And,
    conditions: [
      {
        property: 'environment',
        operator: ConditionOperatorEnum.In,
        value: [Environment.Prod],
      },
      {
        operator: ConditionOperatorEnum.Or,
        conditions: [
          {
            property: 'resourceType',
            operator: ConditionOperatorEnum.In,
            value: [
              ...(demoSettings.AWS ? [ResourceType.AwsRds] : []),
              ...(demoSettings.AZURE
                ? [ResourceType.AzureSqlManagedInstance]
                : []),
              ...(demoSettings.GCP ? [ResourceType.GcpCloudSqlInstance] : []),
            ],
          },
          {
            operator: ConditionOperatorEnum.And,
            conditions: [
              {
                property: 'resourceType',
                operator: ConditionOperatorEnum.In,
                value: [
                  ...(demoSettings.AWS ? [ResourceType.AwsEc2] : []),
                  ...(demoSettings.AZURE
                    ? [ResourceType.AzureVirtualMachine]
                    : []),
                  ...(demoSettings.GCP
                    ? [ResourceType.GcpComputeEngineInstance]
                    : []),
                ],
              },
              {
                property: 'apps',
                operator: ConditionOperatorEnum.ContainsAnyOf,
                value: ['MySQL', 'PostgreSQL', 'MongoDB'],
              },
            ],
          },
        ],
      },
    ],
  };
};

export const backupPoliciesDAL = {
  ...mockDalFactory(backupPoliciesData, (items, demoSettings) => {
    fillDetails(demoSettings);
    return items;
  }),
  getMap: (demoSettings: DemoSettings) => {
    fillDetails(demoSettings);
    return {
      internalSystemsPolicy,
      productionDatabasesPolicy,
      productionWithPIIPolicy,
      productionWithoutPIIPolicy,
    };
  },
};
